import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { motion } from "motion/react";
import { api, useAction } from "../../api.ts";
import { fmtExpiry } from "../../format.ts";
import { Badge, Button, Drawer, ErrorText, Icon, IconButton, cn, useToast } from "../../ui/index.tsx";
import { themeFor } from "../../three/themes.ts";
import { CompatBadges, DetectionBadge, GameArt, GameStatusBadge, NotForSale, startCheckout, useStripeEnabled } from "../_shared.tsx";
import { Springy, rise, stagger, useCalm } from "../member/kit.tsx";
import { fromPrice } from "./model.ts";
import type { Entitlement, Game } from "../../../shared/types.ts";

/** Horizontal strip of every purchasable game. */
export function Store({ games, owned, onPick }: { games: Game[]; owned: Map<string, Entitlement>; onPick: (gameId: string) => void }) {
  const calm = useCalm();
  const strip = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => strip.current?.scrollBy({ left: dir * Math.max(240, strip.current.clientWidth * 0.8), behavior: calm ? "auto" : "smooth" });

  return (
    <section id="store" aria-label="Buy or extend" className="min-w-0 scroll-mt-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold uppercase tracking-wide">Buy or extend</h2>
          <p className="text-xs text-muted">Time is added on top of what you already have.</p>
        </div>
        <div className="hidden gap-1 sm:flex">
          <IconButton icon="chevronLeft" label="Scroll left" onClick={() => scroll(-1)} />
          <IconButton icon="chevronRight" label="Scroll right" onClick={() => scroll(1)} />
        </div>
      </div>
      {games.length === 0 ? (
        <div className="pane px-6 py-10 text-center text-sm text-muted">Nothing for sale right now. Open a support ticket and we'll help you out.</div>
      ) : (
        <motion.div ref={strip} variants={stagger(0.06)} initial="hidden" animate="show"
          className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-3 pt-1">
          {games.map((g) => {
            const own = owned.get(g.id);
            const theme = themeFor(g.id, g.category);
            const price = fromPrice(g);
            return (
              <motion.button key={g.id} type="button" variants={rise} whileHover={calm ? undefined : { y: -4 }} whileTap={{ scale: 0.98 }} onClick={() => onPick(g.id)}
                className="group pane relative w-52 shrink-0 snap-start overflow-hidden text-left transition-colors hover:border-line-strong sm:w-56">
                <div className="relative h-32">
                  <GameArt game={g} className={cn("size-full transition-transform duration-500 group-hover:scale-105", own && !own.active && "opacity-70 grayscale")} />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--color-surface) 4%, transparent 70%), linear-gradient(135deg, ${theme.a}33, transparent 55%)` }} />
                  {own && (
                    <div className="absolute right-2 top-2">
                      <Badge tone={own.active ? "ok" : "bad"} dot>{own.active ? "Owned" : "Expired"}</Badge>
                    </div>
                  )}
                </div>
                <div className="space-y-2 p-3.5 pt-1">
                  <div className="truncate font-display text-base font-semibold uppercase tracking-wide">{g.name}</div>
                  <CompatBadges game={g} />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-muted">
                      {!g.purchasable ? <span className="text-info">Coming soon</span> : price ? <>From <span className="font-semibold text-fg">{price}</span></> : "Unavailable"}
                    </span>
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-accent transition-transform group-hover:translate-x-0.5">
                      {!g.purchasable ? "Notify me" : own ? (own.active ? "Extend" : "Renew") : "View"}<Icon name="chevronRight" className="size-3" />
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      )}
    </section>
  );
}

function BuyPanel({ game, current }: { game: Game; current: Entitlement | undefined }) {
  const navigate = useNavigate();
  const toast = useToast();
  const stripe = useStripeEnabled();
  const [tier, setTier] = useState(0);
  const theme = themeFor(game.id, game.category);
  const lifetimeOwned = !!current?.active && current.expires_at === null;

  const buy = useAction(async () => {
    if (stripe) { await startCheckout(game.id, tier); return; }
    const r = await api.post<{ ticket_id: number }>("/purchase-requests", { game_id: game.id, tier_index: tier });
    toast("Purchase request sent", "success");
    navigate(`/support/${r.ticket_id}`);
  });

  const selected = game.pricing_tiers[tier];

  return (
    <div className="flex min-h-full flex-col">
      <div className="relative -mx-5 -mt-5 h-44 overflow-hidden">
        <GameArt game={game} className="size-full" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, var(--color-bg) 4%, rgb(7 7 10 / 0.35) 70%), linear-gradient(135deg, ${theme.a}40, transparent 60%)` }} />
        <div className="absolute inset-x-5 bottom-3 flex flex-wrap gap-1.5">
          <GameStatusBadge status={game.status} />
          <DetectionBadge status={game.detection_status} />
          <CompatBadges game={game} className="contents" />
        </div>
      </div>

      <div className="mt-4 flex-1 space-y-5">
        {game.description && <p className="text-sm leading-relaxed text-muted">{game.description}</p>}
        {game.features.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {game.features.slice(0, 8).map((f) => <li key={f} className="rounded-md border border-line bg-raised/60 px-2 py-0.5 text-xs text-muted">{f}</li>)}
          </ul>
        )}

        {current && (
          <div className={cn("flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm", current.active ? "border-ok/25 bg-ok/10 text-ok" : "border-bad/25 bg-bad/10 text-bad")}>
            <Icon name={current.active ? "check" : "clock"} className="mt-0.5 size-4 shrink-0" />
            <span>
              {lifetimeOwned ? "You already have lifetime access. Extra time won't add anything." : current.active
                ? `You have ${fmtExpiry(current.expires_at).toLowerCase()}. New time is added on top.`
                : "Your license has expired. Pick a duration to get back in."}
            </span>
          </div>
        )}

        {!game.purchasable ? (
          <>
            <NotForSale game={game} />
            {game.status !== "discontinued" && game.pricing_tiers.length > 0 && (
              <div>
                <div className="kicker mb-2">Launch pricing</div>
                <div className="grid gap-2.5 sm:grid-cols-2">
                  {game.pricing_tiers.map((t, i) => (
                    <div key={`${t.dur}-${i}`} className="rounded-xl border border-line bg-surface p-4 opacity-60">
                      <span className="block text-sm text-muted">{t.dur}</span>
                      <span className="mt-1 block font-display text-2xl font-semibold tabular-nums">{t.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
        <div>
          <div className="kicker mb-2">Choose a duration</div>
          <div role="radiogroup" aria-label="Duration" className="grid gap-2.5 sm:grid-cols-2">
            {game.pricing_tiers.map((t, i) => {
              const on = i === tier;
              return (
                <motion.button key={`${t.dur}-${i}`} type="button" role="radio" aria-checked={on} onClick={() => setTier(i)} whileTap={{ scale: 0.97 }}
                  className={cn("relative rounded-xl border p-4 text-left transition-colors", on ? "border-accent bg-accent-soft shadow-[0_0_28px_-8px_var(--color-accent)]" : "border-line bg-surface hover:border-line-strong hover:bg-raised")}>
                  <span className={cn("absolute right-3 top-3 grid size-5 place-items-center rounded-full border", on ? "border-accent bg-accent text-accent-fg" : "border-line-strong")}>
                    {on && <Icon name="check" className="size-3" />}
                  </span>
                  <span className="block text-sm text-muted">{t.dur}</span>
                  <span className="mt-1 block font-display text-2xl font-semibold tabular-nums">{t.price}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
        )}
      </div>

      {game.purchasable && (
        <div className="sticky bottom-0 -mx-5 -mb-5 mt-6 space-y-3 border-t border-line bg-bg/95 p-5 backdrop-blur">
          <ErrorText>{buy.error}</ErrorText>
          <Springy className="!block">
            <Button variant="primary" size="lg" icon={stripe ? "card" : "send"} className="w-full" loading={buy.pending} disabled={!selected} onClick={() => void buy.run()}>
              {stripe ? "Continue to checkout" : "Request purchase"}{selected && ` · ${selected.price}`}
            </Button>
          </Springy>
          <p className="text-center text-xs text-faint">
            {stripe ? "Pay by card through Stripe. Access is added as soon as the payment clears." : "This opens a support ticket. An admin confirms payment and activates your license."}
          </p>
        </div>
      )}
    </div>
  );
}

export function BuyDrawer({ game, current, onClose }: { game: Game | null; current: Entitlement | undefined; onClose: () => void }) {
  return (
    <Drawer open={!!game} onClose={onClose} title={game ? <span className="font-display uppercase tracking-wide">{game.name}</span> : ""}>
      {game && <BuyPanel key={game.id} game={game} current={current} />}
    </Drawer>
  );
}
